import { Container, Graphics, Sprite } from "pixi.js"
import { Coordinate } from "game-common/models"

/**
 * A building piece landing in the world: a short lift, a fall, and an impact squash with a puff of
 * dust off its foot. Upright pieces (walls) rise and drop; flat pieces (roofs, floors) swell toward
 * the camera and press back down, since moving them up the screen would read as sliding.
 *
 * ONE TIMELINE PER CELL, SHARED BY EVERYONE WHO DRAWS IT. A landing is keyed by storey, layer and
 * cell and lives in `buildLandings`. The builder starts it the moment its plan prints, and its
 * opaque ghost plays it at once. When the server's tile arrives mid-flight, the map poses the real
 * wall from the SAME clock, so the piece never hops. A peer's client finds no landing and starts one
 * when the tile arrives, over the translucent ghost it was showing.
 *
 * GROUPS RIPPLE. A dragged line or a room lands as a wave spreading out from the builder. The
 * wave's spread is capped, so 200 pieces take as long as 12, and every piece's motion is the same
 * size whatever the group's. Dust comes from a per-group budget, not per piece.
 */
export type LandingLayer = "wall" | "roof" | "floor"

export const BUILD_LANDING = {
    enabled: true,
    liftMs: 90,
    fallMs: 120,
    settleMs: 170,
    /** How high an upright piece rises before it drops. */
    liftPx: 7,
    /** How much a flat piece grows toward the camera at the top of its lift. */
    swell: 0.07,
    /** How hard a piece squashes at impact (fraction of its height). */
    squash: 0.09,
    rippleMsPerTile: 40,
    rippleMaxMs: 450,
    /** At most this many pieces of a group raise dust. */
    dustCells: 8,
}
if (typeof window !== "undefined") (window as any).buildLanding = BUILD_LANDING

/** A landed cell stays known this long, so a server tile arriving late does not replay it. */
export const BUILD_LANDING_GRACE_MS = 6000

export const landingImpactMs = (): number => BUILD_LANDING.liftMs + BUILD_LANDING.fallMs
export const landingDurationMs = (): number => landingImpactMs() + BUILD_LANDING.settleMs

export interface LandingPose {
    dy: number
    scaleX: number
    scaleY: number
    alpha: number
}

export const restPose = (): LandingPose => ({ dy: 0, scaleX: 1, scaleY: 1, alpha: 1 })

const easeOut = (k: number): number => 1 - (1 - k) * (1 - k)

/** The pose `elapsed` ms into a landing. `from` is the alpha it rises from (a ghost's translucency). */
export const landingPose = (elapsed: number, layer: LandingLayer, from = 1): LandingPose => {
    const { liftMs, fallMs, settleMs, liftPx, swell, squash } = BUILD_LANDING
    if (!(elapsed > 0) || elapsed >= liftMs + fallMs + settleMs) return restPose()
    const upright = layer === "wall"
    let height: number, alpha = 1
    if (elapsed < liftMs) {
        height = easeOut(elapsed / liftMs)
        alpha = from + (1 - from) * height
    } else if (elapsed < liftMs + fallMs) {
        const k = (elapsed - liftMs) / fallMs
        // Falling, so it gathers speed into the ground.
        height = 1 - k * k
    } else {
        const k = (elapsed - liftMs - fallMs) / settleMs
        // Squashed at contact, a small rebound past rest, then still.
        const s = squash * (1 - k) * (1 - k) * Math.cos(k * Math.PI * 1.5)
        return upright ? { dy: 0, scaleX: 1 + s * 0.5, scaleY: 1 - s, alpha: 1 }
            : { dy: 0, scaleX: 1 - s * 0.4, scaleY: 1 - s * 0.4, alpha: 1 }
    }
    return upright ? { dy: -liftPx * height, scaleX: 1, scaleY: 1, alpha }
        : { dy: 0, scaleX: 1 + swell * height, scaleY: 1 + swell * height, alpha }
}

/**
 * When each cell of a group starts, in ms: a wave spreading from `origin` (in tiles; a cell's centre
 * is at +0.5), or from the group's middle. Nearest first; the whole spread fits in rippleMaxMs.
 */
export const rippleDelays = (cells: readonly Coordinate[], origin?: Coordinate): number[] => {
    if (cells.length < 2) return cells.map(() => 0)
    const from = origin ?? {
        x: cells.reduce((sum, c) => sum + c.x + 0.5, 0) / cells.length,
        y: cells.reduce((sum, c) => sum + c.y + 0.5, 0) / cells.length,
    }
    const distances = cells.map(c => Math.hypot(c.x + 0.5 - from.x, c.y + 0.5 - from.y))
    const nearest = Math.min(...distances)
    const raw = distances.map(d => (d - nearest) * BUILD_LANDING.rippleMsPerTile)
    const widest = Math.max(...raw)
    const fit = widest > BUILD_LANDING.rippleMaxMs ? BUILD_LANDING.rippleMaxMs / widest : 1
    return raw.map(d => Math.round(d * fit))
}

/** Which cells of a group raise dust: all of a small group, else `budget` spread evenly along the wave. */
export const dustPicks = (delays: readonly number[], budget = BUILD_LANDING.dustCells): Set<number> => {
    if (delays.length <= budget) return new Set(delays.map((_, i) => i))
    const order = delays.map((delay, i) => ({ delay, i })).sort((a, b) => a.delay - b.delay || a.i - b.i)
    const picks = new Set<number>()
    for (let n = 0; n < budget; n++) picks.add(order[Math.round(n * (order.length - 1) / (budget - 1))].i)
    return picks
}

/** The dust a material raises: a pale cloud, and chips of the material itself. */
export const landingDustColors = (itemType?: string): { cloud: number; chips: number } => {
    const id = (itemType || "").toLowerCase()
    if (/stone|brick|cobble|rock/.test(id)) return { cloud: 0xcfc8bc, chips: 0x8f887d }
    if (/thatch|straw|hay|reed/.test(id)) return { cloud: 0xd8caa8, chips: 0xd9b95c }
    if (/wood|timber|plank|log|beam/.test(id)) return { cloud: 0xd8caa8, chips: 0xa87a4a }
    return { cloud: 0xd8caa8, chips: 0xa89a80 }
}

export interface Landing {
    x: number
    y: number
    storey: number
    layer: LandingLayer
    startAt: number
    /** How far above its cell the piece is drawn (a roof's uplift), so its dust lands with it. */
    lift: number
    itemType?: string
    /** The tile a peer's client draws its own copy of a flat piece from (the map can't move one cell of a tilemap). */
    tileId?: string
    layerId?: string
    /** 0 none, 1 a little (one of a group), 2 a full puff (a piece placed on its own). */
    dust: 0 | 1 | 2
    dusted?: boolean
    /** The builder's own ghost art, posed with the landing, and the point it squashes toward. */
    ghosts?: Set<Sprite>
    ghostPivot?: { x: number; y: number }
    /** The print was acknowledged: the ghosts are the landing's to put away once the real piece shows. */
    ghostsOwned?: boolean
    releasedAt?: number
}

export interface LandingStart {
    storey: number
    layer: LandingLayer
    now: number
    origin?: Coordinate
    itemType?: string
    lift?: number
    /** Per cell, in `cells` order: the tile and layer to draw a flat piece's copy from. */
    tiles?: { tileId: string; layerId: string }[]
}

const destroyGhosts = (landing: Landing) => {
    landing.ghosts?.forEach(sprite => {
        sprite.parent?.removeChild(sprite)
        if (!sprite.destroyed) sprite.destroy()
    })
    landing.ghosts?.clear()
}

export class BuildLandings {
    private landings = new Map<string, Landing>()

    static key = (storey: number, layer: LandingLayer, x: number, y: number): string => `${storey}:${layer}:${x},${y}`

    /** Land `cells` as one group. Cells already landing (the builder's own print) keep their timeline. */
    start = (cells: readonly Coordinate[], options: LandingStart): Landing[] => {
        if (!BUILD_LANDING.enabled || !cells.length) return []
        const delays = rippleDelays(cells, options.origin)
        const dusty = dustPicks(delays)
        const started: Landing[] = []
        cells.forEach((cell, i) => {
            const key = BuildLandings.key(options.storey, options.layer, cell.x, cell.y)
            if (this.landings.has(key)) return
            const landing: Landing = {
                x: cell.x, y: cell.y, storey: options.storey, layer: options.layer,
                startAt: options.now + delays[i], lift: options.lift ?? 0, itemType: options.itemType,
                tileId: options.tiles?.[i]?.tileId, layerId: options.tiles?.[i]?.layerId,
                dust: !dusty.has(i) ? 0 : cells.length === 1 ? 2 : 1,
            }
            this.landings.set(key, landing)
            started.push(landing)
        })
        return started
    }

    get = (storey: number, layer: LandingLayer, x: number, y: number): Landing | undefined =>
        this.landings.get(BuildLandings.key(storey, layer, x, y))

    /** Still tracked (not pruned or forgotten), so it will put its ghosts away. */
    tracks = (landing: Landing): boolean =>
        this.landings.get(BuildLandings.key(landing.storey, landing.layer, landing.x, landing.y)) === landing

    /** A piece was taken down: building it again lands again. */
    forget = (storey: number, layer: LandingLayer, x: number, y: number): void => {
        const key = BuildLandings.key(storey, layer, x, y)
        const landing = this.landings.get(key)
        if (landing?.ghostsOwned) destroyGhosts(landing)
        this.landings.delete(key)
    }

    forEach = (visit: (landing: Landing) => void): void => this.landings.forEach(visit)

    size = (): number => this.landings.size

    prune = (now: number): void => {
        const expiry = landingDurationMs() + BUILD_LANDING_GRACE_MS
        this.landings.forEach((landing, key) => {
            if (now - landing.startAt < expiry) return
            if (landing.ghostsOwned) destroyGhosts(landing)
            this.landings.delete(key)
        })
    }

    clear = (): void => {
        this.landings.forEach(landing => { if (landing.ghostsOwned) destroyGhosts(landing) })
        this.landings.clear()
    }
}

export const buildLandings = new BuildLandings()

/** Hand a landing's ghosts to it for good: the map destroys them once the real piece shows. */
export const releaseLandingGhosts = (landing: Landing, now: number): void => {
    landing.ghostsOwned = true
    landing.releasedAt = now
}

export const endLandingGhosts = destroyGhosts

// Each posed sprite's resting place, taken the first time it is posed (a sprite is built at rest).
const rests = new WeakMap<Sprite, { x: number; y: number; scaleX: number; scaleY: number }>()

/** Pose sprites about `pivot` (the point a squash presses toward), from where each one rests. */
export const poseSprites = (sprites: Iterable<Sprite>, pose: LandingPose, pivot: { x: number; y: number }, withAlpha = true): void => {
    for (const sprite of sprites) {
        if (sprite.destroyed) continue
        let rest = rests.get(sprite)
        if (!rest) {
            rest = { x: sprite.x, y: sprite.y, scaleX: sprite.scale.x, scaleY: sprite.scale.y }
            rests.set(sprite, rest)
        }
        sprite.scale.set(rest.scaleX * pose.scaleX, rest.scaleY * pose.scaleY)
        sprite.x = pivot.x + (rest.x - pivot.x) * pose.scaleX
        sprite.y = pivot.y + (rest.y + pose.dy - pivot.y) * pose.scaleY
        if (withAlpha) sprite.alpha = pose.alpha
    }
}

/** Put posed sprites back exactly where they rest. */
export const restSprites = (sprites: Iterable<Sprite>, withAlpha = true): void => {
    for (const sprite of sprites) {
        const rest = rests.get(sprite)
        if (!rest) continue
        rests.delete(sprite)
        if (sprite.destroyed) continue
        sprite.scale.set(rest.scaleX, rest.scaleY)
        sprite.position.set(rest.x, rest.y)
        if (withAlpha) sprite.alpha = 1
    }
}

interface Mote {
    graphic: Graphics
    chip: boolean
    x: number
    groundY: number
    height: number
    vx: number
    vh: number
    bornAt: number
    lifeMs: number
    scale: number
    growth: number
    alpha: number
}

const MAX_MOTES = 96
const GRAVITY = 1100

// A small deterministic generator, so one landing's dust is the same every time it is drawn.
const seeded = (seed: number) => () => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

/**
 * The dust off a landed piece's foot: soft clouds that spread along the ground and grow as they fade,
 * and a few chips of the material that hop out and drop. Each mote is drawn once and only moved,
 * so a frame re-tessellates nothing.
 */
export class LandingDust {
    private motes: Mote[] = []

    constructor(private surface: () => Container) {}

    /**
     * A puff along a piece's foot: `x` its middle, `halfWidth` how far along the ground it reaches,
     * `groundY` the line it stands on. Strength 2 is a lone piece, 1 one of a group.
     */
    puff = (x: number, groundY: number, halfWidth: number, colors: { cloud: number; chips: number }, strength: 1 | 2,
        zIndex: number, now: number, seed: number): void => {
        const surface = this.surface()
        if (!surface || surface.destroyed) return
        const random = seeded(seed)
        const between = (min: number, max: number) => min + random() * (max - min)
        const clouds = strength === 2 ? 3 : 2, chips = strength === 2 ? 4 : 2
        for (let i = 0; i < clouds + chips && this.motes.length < MAX_MOTES; i++) {
            const chip = i >= clouds
            const along = between(-halfWidth, halfWidth)
            const side = along < 0 ? -1 : 1
            const graphic = new Graphics()
            if (chip) {
                const size = between(1.2, 2)
                graphic.beginFill(colors.chips, 1).drawRect(-size / 2, -size / 2, size, size).endFill()
            } else {
                // Faint halo, body, dense core: softness without a blur filter.
                for (const [scale, alpha] of [[1, 0.22], [0.68, 0.45], [0.36, 0.9]]) {
                    graphic.beginFill(colors.cloud, alpha).drawEllipse(0, 0, 6 * scale, 3.4 * scale).endFill()
                }
            }
            graphic.zIndex = zIndex
            surface.addChild(graphic)
            this.motes.push({
                graphic, chip, x: x + along, groundY: groundY + between(-1, 1.5), height: chip ? 2 : 0,
                vx: side * (chip ? between(26, 46) : between(12, 24)),
                vh: chip ? between(60, 90) : between(4, 9),
                bornAt: now, lifeMs: chip ? between(320, 420) : between(420, 540),
                scale: chip ? 1 : between(0.45, 0.6), growth: chip ? 1 : between(1.6, 2),
                alpha: chip ? 0.95 : between(0.55, 0.75),
            })
            this.place(this.motes[this.motes.length - 1], 0)
        }
    }

    update = (now: number): void => {
        if (!this.motes.length) return
        this.motes = this.motes.filter(mote => {
            const k = (now - mote.bornAt) / mote.lifeMs
            if (k >= 1 || mote.graphic.destroyed || !mote.graphic.parent) {
                this.retire(mote)
                return false
            }
            this.place(mote, Math.max(0, k))
            return true
        })
    }

    count = (): number => this.motes.length

    clear = (): void => {
        this.motes.forEach(this.retire)
        this.motes = []
    }

    private place = (mote: Mote, k: number) => {
        const seconds = k * mote.lifeMs / 1000
        const graphic = mote.graphic
        if (mote.chip) {
            // Thrown out and up, dropped by gravity; it lies where it lands and fades.
            const landsAt = (mote.vh + Math.sqrt(mote.vh * mote.vh + 2 * GRAVITY * mote.height)) / GRAVITY
            const flight = Math.min(seconds, landsAt)
            const height = Math.max(0, mote.height + mote.vh * flight - GRAVITY * flight * flight / 2)
            graphic.position.set(mote.x + mote.vx * flight, mote.groundY - height)
            graphic.alpha = mote.alpha * (k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4)
        } else {
            // Spreading along the ground and slowing (drag), lifting a little as it thins out.
            const travel = mote.vx * (1 - Math.exp(-4 * seconds)) / 4
            graphic.position.set(mote.x + travel, mote.groundY - mote.vh * seconds)
            graphic.scale.set(mote.scale * (1 + (mote.growth - 1) * easeOut(k)))
            const fadeIn = Math.min(1, k / 0.1)
            graphic.alpha = mote.alpha * fadeIn * (k < 0.35 ? 1 : 1 - ((k - 0.35) / 0.65) ** 1.5)
        }
    }

    private retire = (mote: Mote) => {
        mote.graphic.parent?.removeChild(mote.graphic)
        if (!mote.graphic.destroyed) mote.graphic.destroy()
    }
}
